import { api } from '../baseApi';
import { arrayParamsSerializer } from '../helpers';
import type {
  DownloadReachAnalysisRequest,
  GetAvailableAttributesResponse,
  GetColumnSuggestionsResponse,
  GetColumnsRequest,
  GetColumnsResponse,
  GetPreviewRequest,
  GetPreviewResponse,
  GetReservedColumnsResponse,
  MapDataRequest,
  PublishRequest,
  PublishResponse,
  SegmentationRequest,
  SegmentationResponse,
} from '../listMatching/dto';
import { makeRequest } from '../makeRequest';
import type { IMedscapeMetadata } from '../workspaces';
import type {
  GetAvailableAttributesConsentRequest,
  GetColumnSuggestionsConsentRequest,
  MapDataConsentResponse,
  SoftMatchProcessRequest,
  SoftMatchProcessResponse,
} from './dto';
import { ListMatchingConsentAPIRoutes } from './route';

const SOFT_MATCH_PROCESS_ALWAYS_SEND_FIELDS = [
  'npiListId',
  'workspaceID',
  'reachType',
  'countriesAllowed',
  'statesSuppression',
];

const isEmptyRequestValue = (value: unknown) =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

export function softMatchProcess(
  body: SoftMatchProcessRequest,
  medscapeData?: IMedscapeMetadata,
): Promise<SoftMatchProcessResponse> {
  const { deliverable, ...medscapeMetadata } = medscapeData ?? {};

  const payload = Object.fromEntries(
    Object.entries({ ...body, ...medscapeMetadata, deliverableID: deliverable }).filter(
      ([key, value]) => SOFT_MATCH_PROCESS_ALWAYS_SEND_FIELDS.includes(key) || !isEmptyRequestValue(value),
    ),
  );

  return makeRequest(api.post(ListMatchingConsentAPIRoutes.softMatchProcess(), payload));
}

export function getColumns(params: GetColumnsRequest): Promise<GetColumnsResponse> {
  return makeRequest(
    api.get(ListMatchingConsentAPIRoutes.columns(), {
      params,
    }),
  );
}

export function getAvailableAttributes(
  params: GetAvailableAttributesConsentRequest = {},
): Promise<GetAvailableAttributesResponse> {
  return makeRequest(
    api.get(ListMatchingConsentAPIRoutes.availableAttributes(), {
      params,
      ...(params.workspaceID ? { headers: { bi_workspace_id: String(params.workspaceID) } } : {}),
    }),
  );
}

export function mapData(body: MapDataRequest, medscapeData?: IMedscapeMetadata): Promise<MapDataConsentResponse> {
  const { deliverable, ...medscapeMetadata } = medscapeData ?? {};

  return makeRequest(
    api.post(ListMatchingConsentAPIRoutes.mapData(), {
      ...body,
      ...medscapeMetadata,
      deliverableID: deliverable,
    }),
  );
}

export function getColumnSuggestions(
  params: GetColumnSuggestionsConsentRequest,
): Promise<GetColumnSuggestionsResponse> {
  return makeRequest(
    api.get(ListMatchingConsentAPIRoutes.columnSuggestions(), {
      params,
      paramsSerializer: arrayParamsSerializer,
    }),
  );
}

export function updateSegmentation(
  body: SegmentationRequest,
  medscapeData?: IMedscapeMetadata,
): Promise<SegmentationResponse> {
  const { deliverable, ...medscapeMetadata } = medscapeData ?? {};

  return makeRequest(
    api.post(ListMatchingConsentAPIRoutes.segmentation(), {
      ...body,
      ...medscapeMetadata,
      segments: body.segments.map((segment) => ({
        ...segment,
        name: segment.id,
        id: undefined,
      })),
      deliverableID: deliverable,
    }),
  );
}

export function getPreview(params: GetPreviewRequest): Promise<GetPreviewResponse> {
  return makeRequest(api.get(ListMatchingConsentAPIRoutes.preview(), { params }));
}

export function getReservedColumns(): Promise<GetReservedColumnsResponse> {
  return makeRequest(api.get(ListMatchingConsentAPIRoutes.reservedColumns()));
}

export function publish(params: PublishRequest): Promise<PublishResponse> {
  return makeRequest(
    api.get(ListMatchingConsentAPIRoutes.publish(), {
      params,
    }),
  );
}

export function downloadReachAnalysis(params: DownloadReachAnalysisRequest): Promise<string> {
  return makeRequest(
    api.get(ListMatchingConsentAPIRoutes.download(), {
      responseType: 'blob',
      params,
      transformResponse: (data) => {
        return URL.createObjectURL(data);
      },
    }),
  );
}
